import { useState, useEffect, useCallback } from "react";
import { SelectChangeEvent } from "@mui/material/Select";
import { Segment, SegmentationMethod, SegmentationGroup } from "../types/types";
import { MAX_SEGMENTATIONS } from "../common/constants";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { ColumnRange } from "../components/segmentation/ManualSegmentationModal";

interface UseSegmentationManagementProps {
  segmentationTypes: string[];
  tableName: string;
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
  columnRanges: Record<string, ColumnRange>;
}

interface SegmentationManagement {
  segmentGroups: SegmentationGroup[];
  modalOpen: number | null;
  localIsCollapsed: boolean;
  handleAddSegment: () => void;
  handleRemoveSegment: (index: number) => void;
  handleTypeChange: (event: SelectChangeEvent<string>, index: number) => void;
  handleMethodChange: (event: React.ChangeEvent<HTMLInputElement>, index: number) => void;
  handleRangeColumnChange: (event: SelectChangeEvent<string>, index: number) => void;
  handleModalClose: () => void;
  handleCreateSegments: (segments: Segment[], index: number, method: SegmentationMethod) => void;
  toggleCollapse: () => void;
  shouldDisableSwitch: (index: number) => boolean;
  handleEdit: (index: number) => void;
  columnRanges: Record<string, ColumnRange>;
}

export const useSegmentationManagement = ({
  segmentationTypes,
  tableName,
  isCollapsed,
  setIsCollapsed,
  columnRanges,
}: UseSegmentationManagementProps): SegmentationManagement => {
  const {
    segmentGroups,
    addSegmentGroup,
    removeSegmentGroup,
    updateSegmentGroupType,
    updateSegmentGroupMethod,
    updateSegmentGroupSegments,
    updateSegmentGroupRangeColumn,
  } = useHeadroomContext();
  const [modalOpen, setModalOpen] = useState<number | null>(null);

  useEffect(() => {
    setIsCollapsed(isCollapsed);
  }, [isCollapsed, setIsCollapsed]);

  const handleAddSegment = useCallback(() => {
    if (segmentGroups.length < MAX_SEGMENTATIONS) {
      addSegmentGroup();
    }
  }, [segmentGroups, addSegmentGroup]);

  const handleRemoveSegment = useCallback(
    (index: number) => {
      if (segmentGroups.length > 1) {
        removeSegmentGroup(index);
      }
    },
    [segmentGroups, removeSegmentGroup]
  );

  const handleTypeChange = useCallback(
    (event: SelectChangeEvent<string>, index: number) => {
      const newType = event.target.value;
      const isNumeric = columnRanges[newType]?.isNumeric || false;
      updateSegmentGroupType(index, newType);
      if (!isNumeric) {
        updateSegmentGroupSegments(index, []);
        updateSegmentGroupMethod(index, "DATA");
      }
    },
    [updateSegmentGroupType, updateSegmentGroupSegments, updateSegmentGroupMethod, columnRanges]
  );

  const handleMethodChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const method: SegmentationMethod = event.target.checked ? "MANUAL" : "DATA";
      updateSegmentGroupMethod(index, method);
      if (method === "DATA") {
        updateSegmentGroupSegments(index, []);
      }
    },
    [updateSegmentGroupMethod, updateSegmentGroupSegments]
  );

  const handleEdit = useCallback((index: number) => {
    setModalOpen(index);
  }, []);

  const handleRangeColumnChange = useCallback(
    (event: SelectChangeEvent<string>, index: number) => {
      const newRangeColumn = event.target.value;
      updateSegmentGroupRangeColumn(index, newRangeColumn);
    },
    [updateSegmentGroupRangeColumn]
  );

  const handleModalClose = useCallback(() => {
    setModalOpen(null);
  }, []);

  const handleCreateSegments = useCallback(
    (segments: Segment[], index: number, method: SegmentationMethod) => {
      updateSegmentGroupMethod(index, method);
      updateSegmentGroupSegments(index, segments);
      setModalOpen(null);
    },
    [updateSegmentGroupMethod, updateSegmentGroupSegments]
  );

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [setIsCollapsed, isCollapsed]);

  const shouldDisableSwitch = useCallback(
    (index: number) => {
      const group = segmentGroups.find((group) => group.index === index);
      return !group?.type || !tableName || !columnRanges[group?.type]?.isNumeric;
    },
    [segmentGroups, tableName, columnRanges]
  );

  return {
    segmentGroups,
    modalOpen,
    localIsCollapsed: isCollapsed,
    handleAddSegment,
    handleRemoveSegment,
    handleTypeChange,
    handleMethodChange,
    handleRangeColumnChange,
    handleModalClose,
    handleCreateSegments,
    toggleCollapse,
    shouldDisableSwitch,
    handleEdit,
    columnRanges,
  };
};
